import {
  takeLatest,
  call,
  put,
  all,
  select,
  takeLeading
} from 'redux-saga/effects';
import { AxiosResponse } from 'axios';
import { UserData, FetchStatus } from '../types';
import {
  XAuthAction,
  setUserData,
  setUserFetchStatus,
  signOutUser,
  refreshUserToken
} from './actions';
import Api, { RefreshArgs, SignInArgs } from '../api/user';

type SagaGenerator<TType = void, TPayload = void, TMeta = void> = ({
  type,
  payload
}: {
  type: TType;
  payload?: TPayload;
  meta?: TMeta;
}) => void;

type SagaGeneratorDefault<TType = void, TPayload = void, TMeta = void> = ({
  type,
  payload
}: {
  type: TType;
  payload: TPayload;
  meta?: TMeta;
}) => void;

interface SignInGenArgs {
  type?: XAuthAction;
  payload: SignInArgs;
}

interface RefreshUserGenMeta {
  onSuccess: () => void;
}

interface RefreshUserGenArgs {
  type?: XAuthAction;
  payload: RefreshArgs;
  meta?: RefreshUserGenMeta;
}

function* handleSignInSuccess(data: {
  accessToken: string;
  refreshToken: string;
  userBO: UserData;
}) {
  const { accessToken, refreshToken: userRefreshToken, userBO } = data;

  yield call([localStorage, localStorage.setItem], 'jwt', accessToken);
  yield call(
    [localStorage, localStorage.setItem],
    'refreshToken',
    userRefreshToken
  );

  yield put(
    setUserData({
      data: userBO,
      jwt: accessToken,
      refreshToken: userRefreshToken
    })
  );
  yield put(setUserFetchStatus(FetchStatus.SUCCESS));
}

function* signIn({ payload }: SignInGenArgs) {
  const { login, password } = payload;
  yield put(setUserFetchStatus(FetchStatus.FETCHING));

  try {
    const response: AxiosResponse = yield call(Api.signIn, { login, password });
    const { status, data } = response;

    if (status === 200) {
      yield call(handleSignInSuccess, data);
    } else {
      throw new Error();
    }
  } catch (err) {
    yield put(setUserFetchStatus(FetchStatus.ERROR));

    yield put(
      setUserData({
        data: { errorStatus: err.response.status },
        jwt: '',
        refreshToken: ''
      })
    );
  }
}

function* signOut() {
  yield call([localStorage, localStorage.removeItem], 'jwt');
  yield call([localStorage, localStorage.removeItem], 'refreshToken');
}

function* refreshToken({ payload, meta }: RefreshUserGenArgs) {
  try {
    const response: AxiosResponse = yield call(Api.refreshJWT, payload);
    const { status, data } = response;

    if (status === 200) {
      yield call(handleSignInSuccess, data);

      if (meta?.onSuccess) {
        meta.onSuccess();
      }
    } else {
      throw new Error();
    }
  } catch (e) {
    yield put(signOutUser());
  }
}

function* tryRefresh(
  jwt: string | undefined,
  refToken: string | undefined,
  onSuccess?: () => void
) {
  if (jwt && refToken) {
    yield put(refreshUserToken({ jwt, refreshToken: refToken }, onSuccess));
  } else {
    yield put(signOutUser());
  }
}

function* checkLSJWT() {
  yield put(setUserFetchStatus(FetchStatus.USER_AUTH_CHECK));

  const jwtLS: string | undefined = yield call(
    [localStorage, localStorage.getItem],
    'jwt'
  );
  const refreshTokenLS: string | undefined = yield call(
    [localStorage, localStorage.getItem],
    'refreshToken'
  );

  yield tryRefresh(jwtLS, refreshTokenLS);
}

function* refreshCurrentToken({ meta }: { meta?: { onSuccess: () => void } }) {
  const { jwt, refreshToken: refToken } = yield select((x) => x.user);

  yield tryRefresh(jwt, refToken, meta?.onSuccess);
}

function* watchUser() {
  yield takeLeading<XAuthAction, SagaGeneratorDefault<XAuthAction, SignInArgs>>(
    XAuthAction.SIGN_IN,
    signIn
  );
  yield takeLatest<XAuthAction, SagaGeneratorDefault<XAuthAction>>(
    XAuthAction.SIGN_OUT,
    signOut
  );
  yield takeLeading<XAuthAction, SagaGeneratorDefault<XAuthAction>>(
    XAuthAction.CHECK_LS_JWT,
    checkLSJWT
  );
  yield takeLeading<
    XAuthAction,
    SagaGenerator<XAuthAction, void, RefreshUserGenMeta>
  >(XAuthAction.REFRESH_CURRENT_USER_TOKEN, refreshCurrentToken);
  yield takeLeading<
    XAuthAction,
    SagaGeneratorDefault<XAuthAction, RefreshArgs, RefreshUserGenMeta>
  >(XAuthAction.REFRESH_USER_TOKEN, refreshToken);
}

export default function* rootSaga() {
  yield all([watchUser()]);
}
